/**
 * POST /v2/portfolio/rebalance-questionnaire/submit.
 *
 * Belongs here: path, body, and the mutation.
 * Does not belong here: loading overlay or navigation (the page owns those).
 */
import { useMutation } from '@tanstack/react-query'
import axiosInstance from '../../../../utils/axiosConfig'
import { mobileBackendUrl } from '../../../../env'
import { buildStrategyQuestionInputs } from '../../expert-review/api/useSubmitStrategyAnswersMutation'
import type {
  Question,
  StrategyQuestionnaireAnswersMap
} from '../../expert-review/shared/types/questionnaire.types'

export const REBALANCE_QUESTIONNAIRE_SUBMIT_PATH = `${mobileBackendUrl}/v2/portfolio/rebalance-questionnaire/submit`

export interface RebalanceQuestionInput {
  questionId: string
  optionId: string[]
}

export interface SubmitRebalanceQuestionnaireRequest {
  questionnaireId: string
  questionInputs: RebalanceQuestionInput[]
}

export interface SubmitRebalanceQuestionnaireResponse {
  riskProfile?: string
  /** Native navigation for View plan. Done is only the fallback when this is missing. */
  viewPlanCta?: Record<string, unknown> | null
}

/** Strategy submit body, limited to visible questions and option ids. This POST has no amount field. */
export function buildRebalanceQuestionInputs(
  visibleQuestions: Question[],
  answers: StrategyQuestionnaireAnswersMap
): RebalanceQuestionInput[] {
  const visibleIds = new Set(
    visibleQuestions.map((question) => question?.id).filter((questionId) => questionId != null && questionId !== '')
  )
  return buildStrategyQuestionInputs(answers).flatMap((input) => {
    const optionId = input.optionId ?? []
    if (!visibleIds.has(input.questionId) || optionId.length === 0) return []
    return [{ questionId: input.questionId, optionId }]
  })
}

/** POST answers. Returns the body; caller checks `riskProfile`. */
export async function submitRebalanceQuestionnaire(
  body: SubmitRebalanceQuestionnaireRequest
): Promise<SubmitRebalanceQuestionnaireResponse> {
  const response = await axiosInstance.post<SubmitRebalanceQuestionnaireResponse>(
    REBALANCE_QUESTIONNAIRE_SUBMIT_PATH,
    body
  )
  return response?.data ?? {}
}

export function useSubmitRebalanceQuestionnaireMutation() {
  return useMutation({
    mutationFn: submitRebalanceQuestionnaire,
    retry: false
  })
}
